import { Box, CircleCheck, Lock, Shield, Truck } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * One grid for the section legend and every row under it
 * (CHG-2026-09-menu-editor-redesign). The two were separate flex rows in the first draft and
 * their columns drifted apart — "Verkäuflich" ended up above the price field. Keeping a single
 * track list here makes that impossible: change a width once and both move together.
 *
 * Narrow, the ledger collapses to Verkäuflich alone and the four terms live only in the row's
 * tooltip — the pre-change behaviour, kept as a floor rather than as the design.
 *
 * Measured, not guessed: with the sidebar and the 384px product panel, a 1600px window leaves the
 * row about 814px. The first draft spent 626px of that on fixed columns and left 82px for the name
 * input. So the operators got their own tracks removed — the numbers carry their sign instead, a
 * rule separates the result — and the collapse is a CONTAINER query, because the row's width has
 * little to do with the viewport's.
 */
export const LEDGER_GRID = [
  'grid items-center gap-x-2',
  // Under ~460px the product panel has taken so much of the editor that even the toggle no
  // longer fits: name, sellable, price, overflow. Anything more overflows the card instead of
  // shrinking — measured at a 1150px window, where the editor column is only 364px wide.
  'grid-cols-[minmax(130px,1fr)_62px_88px_26px]',
  '@[460px]:grid-cols-[minmax(150px,1fr)_66px_62px_88px_26px]',
  // Wide: the name is CAPPED and a flexible spacer sits between it and the ledger. On a bare 1fr
  // the name swallowed every surplus pixel — at 1920 it grew past 600px while the numbers stayed
  // in their narrow tracks.
  //
  // The four term columns are all EXACTLY 70px. They were 60/62/70/60, and because the values are
  // right-aligned that put their right edges 76, 84 and 74px apart: a gap that looks mis-set even
  // though every grid gap is identical. Equal tracks are what make a column of figures read as a
  // column. Change one, change all four.
  '@[800px]:gap-x-3 @[800px]:grid-cols-[minmax(150px,560px)_64px_1fr_70px_70px_70px_70px_1px_74px_84px_26px]',
].join(' ')

/** Every ledger cell except Verkäuflich disappears with the narrow layout. */
const HIDE_NARROW = 'hidden @[800px]:block'

/** The temperature toggle is the first thing to go when even the narrow grid stops fitting. */
export const HIDE_TIGHT = 'hidden @[460px]:flex'

/** Icon colours carry the identity in the rows, where the labels are not repeated. */
const INK = {
  onHand: 'text-foreground',
  reserved: 'text-[oklch(0.55_0.10_250)]',
  incoming: 'text-[oklch(0.47_0.10_155)]',
  buffer: 'text-muted-foreground',
  sellable: 'text-foreground',
} as const

function LegendCell({
  icon: Icon,
  label,
  hint,
  ink,
}: {
  icon: typeof Box
  label: string
  hint: string
  ink: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'hidden cursor-help items-center justify-end gap-1 whitespace-nowrap @[800px]:flex',
            ink,
          )}
        >
          <Icon className="h-3 w-3" />
          <span className="text-[10px] font-semibold tracking-wide">
            {label}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{hint}</TooltipContent>
    </Tooltip>
  )
}

/** The hairline that separates the terms from the result, standing in for the "=" sign. */
export function LedgerRule() {
  return <div aria-hidden className={cn(HIDE_NARROW, 'h-5 w-px bg-border')} />
}

/**
 * Names the four terms once per section so the rows below can be numbers alone.
 *
 * The incoming column means the MENU'S day, never today (RULE-CLOUD-INV-010). The label used to
 * carry the date, but "Eing. 18.09." needs ~72px and the term columns are 70px each — and equal
 * widths are what keep the figures aligned. The date sits in the page header above the section,
 * and the column's own tooltip spells out which day it means.
 */
export function StockLedgerLegend() {
  const { t } = useTranslation('menus')
  return (
    // The transparent border is load-bearing: the rows below carry a 1px border, so without a
    // matching one here every column would sit 2px off from the label above it.
    <div
      className={cn(
        LEDGER_GRID,
        'border border-transparent px-3 pb-2 text-muted-foreground',
      )}
    >
      <div />
      <div
        className={cn(
          HIDE_TIGHT,
          'col-start-2 justify-center text-[10px] font-semibold uppercase tracking-wide',
        )}
      >
        {t('itemRow.temperature')}
      </div>
      <div className={HIDE_NARROW} />
      <LegendCell
        icon={Box}
        label={t('itemRow.onHand')}
        hint={t('itemRow.onHandHint')}
        ink={INK.onHand}
      />
      <LegendCell
        icon={Lock}
        label={t('itemRow.reserved')}
        hint={t('itemRow.reservedHint')}
        ink={INK.reserved}
      />
      <LegendCell
        icon={Truck}
        label={t('itemRow.incoming')}
        hint={t('itemRow.incomingHint')}
        ink={INK.incoming}
      />
      <LegendCell
        icon={Shield}
        label={t('itemRow.buffer')}
        hint={t('itemRow.bufferHint')}
        ink={INK.buffer}
      />
      <div className={HIDE_NARROW} />
      <LegendCell
        icon={CircleCheck}
        label={t('itemRow.sellable')}
        hint={t('itemRow.sellableHint')}
        ink={INK.sellable}
      />
      <div className="col-start-3 text-right text-[10px] font-semibold uppercase tracking-wide @[460px]:col-start-4 @[800px]:col-start-10">
        {t('itemRow.price')}
      </div>
    </div>
  )
}

/** One right-aligned ledger number. Tabular figures so the columns line up down the section. */
export function Num({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('text-right text-sm tabular-nums', className)}>
      {children}
    </div>
  )
}

export { HIDE_NARROW, INK }
